import apiService from '../apiService';

// ========== 測驗管理 ==========

/**
 * 獲取測驗列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @returns {Promise} API 響應
 */
export const getQuizzes = async (params = {}) => {
    const url = '/api/admin/quizzes';
    return apiService.GET(url, params);
};

/**
 * 創建新測驗
 * @param {Object} quizData - 測驗資料
 * @param {string} quizData.title - 測驗標題
 * @param {string} quizData.description - 測驗描述
 * @returns {Promise} API 響應
 */
export const createQuiz = async (quizData) => {
    const url = '/api/admin/quizzes';
    return apiService.POST(url, quizData);
};

/**
 * 獲取單一測驗詳情
 * @param {number} quizId - 測驗 ID
 * @returns {Promise} API 響應
 */
export const getQuizDetail = async (quizId) => {
    const url = `/api/admin/quizzes/${quizId}`;
    return apiService.GET(url);
};

/**
 * 更新測驗
 * @param {number} quizId - 測驗 ID
 * @param {Object} quizData - 要更新的測驗資料
 * @returns {Promise} API 響應
 */
export const updateQuiz = async (quizId, quizData) => {
    const url = `/api/admin/quizzes/${quizId}`;
    return apiService.PUT(url, quizData);
};

/**
 * 刪除測驗
 * @param {number} quizId - 測驗 ID
 * @returns {Promise} API 響應
 */
export const deleteQuiz = async (quizId) => {
    const url = `/api/admin/quizzes/${quizId}`;
    return apiService.DELETE(url);
};

/**
 * 複製測驗
 * @param {number} quizId - 測驗 ID
 * @returns {Promise} API 響應
 */
export const duplicateQuiz = async (quizId) => {
    const url = `/api/admin/quizzes/${quizId}/duplicate`;
    return apiService.POST(url);
};

// ========== 題目管理 ==========

/**
 * 獲取測驗題目列表
 * @param {number} quizId - 測驗 ID
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getQuizQuestions = async (quizId, params = {}) => {
    const url = `/api/admin/quizzes/${quizId}/questions`;
    return apiService.GET(url, params);
};

/**
 * 創建測驗題目
 * @param {number} quizId - 測驗 ID
 * @param {Object} questionData - 題目資料
 * @param {string} questionData.question_text - 題目內容
 * @param {string} questionData.question_type - 題目類型
 * @returns {Promise} API 響應
 */
export const createQuizQuestion = async (quizId, questionData) => {
    const url = `/api/admin/quizzes/${quizId}/questions`;
    return apiService.POST(url, questionData);
};

/**
 * 獲取單一題目詳情
 * @param {number} quizId - 測驗 ID
 * @param {number} questionId - 題目 ID
 * @returns {Promise} API 響應
 */
export const getQuestionDetail = async (quizId, questionId) => {
    const url = `/api/admin/quizzes/${quizId}/questions/${questionId}`;
    return apiService.GET(url);
};

/**
 * 更新題目
 * @param {number} quizId - 測驗 ID
 * @param {number} questionId - 題目 ID
 * @param {Object} questionData - 要更新的題目資料
 * @returns {Promise} API 響應
 */
export const updateQuestion = async (quizId, questionId, questionData) => {
    const url = `/api/admin/quizzes/${quizId}/questions/${questionId}`;
    return apiService.PUT(url, questionData);
};

/**
 * 刪除題目
 * @param {number} quizId - 測驗 ID
 * @param {number} questionId - 題目 ID
 * @returns {Promise} API 響應
 */
export const deleteQuestion = async (quizId, questionId) => {
    const url = `/api/admin/quizzes/${quizId}/questions/${questionId}`;
    return apiService.DELETE(url);
};

/**
 * 批量更新題目順序
 * @param {number} quizId - 測驗 ID
 * @param {Object} orderData - 順序資料
 * @param {Array} orderData.question_orders - 題目順序陣列
 * @returns {Promise} API 響應
 */
export const updateQuestionsOrder = async (quizId, orderData) => {
    const url = `/api/admin/quizzes/${quizId}/questions/batch-update-order`;
    return apiService.PUT(url, orderData);
};

// ========== 選項管理 ==========

/**
 * 獲取題目選項列表
 * @param {number} questionId - 題目 ID
 * @returns {Promise} API 響應
 */
export const getQuestionOptions = async (questionId) => {
    const url = `/api/admin/questions/${questionId}/options`;
    return apiService.GET(url);
};

/**
 * 創建題目選項
 * @param {number} questionId - 題目 ID
 * @param {Object} optionData - 選項資料
 * @param {string} optionData.option_text - 選項內容
 * @param {boolean} optionData.is_correct - 是否為正確答案
 * @returns {Promise} API 響應
 */
export const createQuestionOption = async (questionId, optionData) => {
    const url = `/api/admin/questions/${questionId}/options`;
    return apiService.POST(url, optionData);
};

/**
 * 獲取單一選項詳情
 * @param {number} questionId - 題目 ID
 * @param {number} optionId - 選項 ID
 * @returns {Promise} API 響應
 */
export const getOptionDetail = async (questionId, optionId) => {
    const url = `/api/admin/questions/${questionId}/options/${optionId}`;
    return apiService.GET(url);
};

/**
 * 更新選項
 * @param {number} questionId - 題目 ID
 * @param {number} optionId - 選項 ID
 * @param {Object} optionData - 要更新的選項資料
 * @returns {Promise} API 響應
 */
export const updateOption = async (questionId, optionId, optionData) => {
    const url = `/api/admin/questions/${questionId}/options/${optionId}`;
    return apiService.PUT(url, optionData);
};

/**
 * 刪除選項
 * @param {number} questionId - 題目 ID
 * @param {number} optionId - 選項 ID
 * @returns {Promise} API 響應
 */
export const deleteOption = async (questionId, optionId) => {
    const url = `/api/admin/questions/${questionId}/options/${optionId}`;
    return apiService.DELETE(url);
};

// ========== 作答記錄 ==========

/**
 * 獲取測驗作答記錄列表
 * @param {Object} params - 查詢參數
 * @param {number} params.page - 頁碼
 * @param {number} params.per_page - 每頁筆數
 * @param {number} params.quiz_id - 測驗 ID (可選)
 * @param {number} params.user_id - 用戶 ID (可選)
 * @returns {Promise} API 響應
 */
export const getQuizAttempts = async (params = {}) => {
    const url = '/api/admin/quiz-attempts';
    return apiService.GET(url, params);
};

/**
 * 獲取單一作答記錄詳情
 * @param {number} attemptId - 作答記錄 ID
 * @returns {Promise} API 響應
 */
export const getQuizAttemptDetail = async (attemptId) => {
    const url = `/api/admin/quiz-attempts/${attemptId}`;
    return apiService.GET(url);
};

/**
 * 更新作答記錄
 * @param {number} attemptId - 作答記錄 ID
 * @param {Object} attemptData - 要更新的作答記錄資料
 * @returns {Promise} API 響應
 */
export const updateQuizAttempt = async (attemptId, attemptData) => {
    const url = `/api/admin/quiz-attempts/${attemptId}`;
    return apiService.PUT(url, attemptData);
};

/**
 * 刪除作答記錄
 * @param {number} attemptId - 作答記錄 ID
 * @returns {Promise} API 響應
 */
export const deleteQuizAttempt = async (attemptId) => {
    const url = `/api/admin/quiz-attempts/${attemptId}`;
    return apiService.DELETE(url);
};

/**
 * 獲取作答記錄的詳細答案
 * @param {number} attemptId - 作答記錄 ID
 * @returns {Promise} API 響應
 */
export const getQuizAttemptAnswers = async (attemptId) => {
    const url = `/api/admin/quiz-attempts/${attemptId}/answers`;
    return apiService.GET(url);
};

// ========== 測驗維度 ==========

/**
 * 獲取測驗維度列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getQuizDimensions = async (params = {}) => {
    const url = '/api/admin/quiz-dimensions';
    return apiService.GET(url, params);
};

/**
 * 創建測驗維度
 * @param {Object} dimensionData - 維度資料
 * @param {string} dimensionData.name - 維度名稱
 * @param {string} dimensionData.description - 維度描述
 * @returns {Promise} API 響應
 */
export const createQuizDimension = async (dimensionData) => {
    const url = '/api/admin/quiz-dimensions';
    return apiService.POST(url, dimensionData);
};

/**
 * 獲取單一測驗維度詳情
 * @param {number} dimensionId - 維度 ID
 * @returns {Promise} API 響應
 */
export const getQuizDimensionDetail = async (dimensionId) => {
    const url = `/api/admin/quiz-dimensions/${dimensionId}`;
    return apiService.GET(url);
};

/**
 * 更新測驗維度
 * @param {number} dimensionId - 維度 ID
 * @param {Object} dimensionData - 要更新的維度資料
 * @returns {Promise} API 響應
 */
export const updateQuizDimension = async (dimensionId, dimensionData) => {
    const url = `/api/admin/quiz-dimensions/${dimensionId}`;
    return apiService.PUT(url, dimensionData);
};

/**
 * 刪除測驗維度
 * @param {number} dimensionId - 維度 ID
 * @returns {Promise} API 響應
 */
export const deleteQuizDimension = async (dimensionId) => {
    const url = `/api/admin/quiz-dimensions/${dimensionId}`;
    return apiService.DELETE(url);
};

// ========== 測驗結果卡 ==========

/**
 * 獲取測驗結果卡列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getQuizResults = async (params = {}) => {
    const url = '/api/admin/quiz-results';
    return apiService.GET(url, params);
};

/**
 * 創建測驗結果卡
 * @param {FormData} resultData - 結果卡資料（FormData，支援檔案上傳）
 * @returns {Promise} API 響應
 */
export const createQuizResult = async (resultData) => {
    const url = '/api/admin/quiz-results';
    return apiService.POST(url, resultData);
};

/**
 * 獲取單一測驗結果卡詳情
 * @param {number} resultId - 結果卡 ID
 * @returns {Promise} API 響應
 */
export const getQuizResultDetail = async (resultId) => {
    const url = `/api/admin/quiz-results/${resultId}`;
    return apiService.GET(url);
};

/**
 * 更新測驗結果卡
 * @param {number} resultId - 結果卡 ID
 * @param {FormData} resultData - 要更新的結果卡資料（FormData，支援檔案上傳）
 * @returns {Promise} API 響應
 */
export const updateQuizResult = async (resultId, resultData) => {
    const url = `/api/admin/quiz-results/${resultId}`;
    // 使用 POST 方法配合 _method=PUT（推薦，用於檔案上傳）
    // 確保 FormData 中包含 _method=PUT
    if (resultData instanceof FormData && !resultData.has('_method')) {
        resultData.append('_method', 'PUT');
    }
    return apiService.POST(url, resultData);
};

/**
 * 刪除測驗結果卡
 * @param {number} resultId - 結果卡 ID
 * @returns {Promise} API 響應
 */
export const deleteQuizResult = async (resultId) => {
    const url = `/api/admin/quiz-results/${resultId}`;
    return apiService.DELETE(url);
};

// ========== 審核管理 ==========

/**
 * 獲取測驗審核列表
 * @param {Object} params - 查詢參數
 * @returns {Promise} API 響應
 */
export const getQuizReviews = async (params = {}) => {
    const url = '/api/admin/quiz-reviews';
    return apiService.GET(url, params);
};

/**
 * 創建測驗審核
 * @param {Object} reviewData - 審核資料
 * @returns {Promise} API 響應
 */
export const createQuizReview = async (reviewData) => {
    const url = '/api/admin/quiz-reviews';
    return apiService.POST(url, reviewData);
};

/**
 * 獲取單一測驗審核詳情
 * @param {number} reviewId - 審核 ID
 * @returns {Promise} API 響應
 */
export const getQuizReviewDetail = async (reviewId) => {
    const url = `/api/admin/quiz-reviews/${reviewId}`;
    return apiService.GET(url);
};

/**
 * 更新測驗審核
 * @param {number} reviewId - 審核 ID
 * @param {Object} reviewData - 要更新的審核資料
 * @returns {Promise} API 響應
 */
export const updateQuizReview = async (reviewId, reviewData) => {
    const url = `/api/admin/quiz-reviews/${reviewId}`;
    return apiService.PUT(url, reviewData);
};

/**
 * 刪除測驗審核
 * @param {number} reviewId - 審核 ID
 * @returns {Promise} API 響應
 */
export const deleteQuizReview = async (reviewId) => {
    const url = `/api/admin/quiz-reviews/${reviewId}`;
    return apiService.DELETE(url);
};
